import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  MatListOption,
  MatSelectionList,
  MatSelectionListChange,
} from '@angular/material/list';
import { YearAndQuartalSelectionComponent } from '../year-and-quartal-selection/year-and-quartal-selection.component';
import { MonthSelectionComponent } from '../month-selection/month-selection.component';
import { WeekSelectionComponent } from '../week-selection/week-selection.component';
import { DateRangeSelectionComponent } from '../date-range-selection/date-range-selection.component';
import { DateSelectionTypeEnum } from '../../core/enums/date-selection-type.enum';
import { DateRange } from '../../core/models/date-range';
import { TextKeys } from '../../core/i18n/text-keys';
import * as moment from 'moment';
import { UntypedFormControl } from '@angular/forms';
import { TIMEZONE } from '../../core/services/language.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-time-frame-selections',
  templateUrl: './time-frame-selections.component.html',
  styleUrls: ['./time-frame-selections.component.scss'],
})
export class TimeFrameSelectionsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() showTotal: boolean;

  @Input() defaultDate: DateRange;
  @Input() defaultItem: DateSelectionTypeEnum;

  @Input() istimeFrameVisible = true;
  @Output() dateRangeChanged = new EventEmitter<DateRange>();

  @ViewChild('selectionList') selectionList: MatSelectionList;
  @ViewChild('yearSelection') yearSelection: YearAndQuartalSelectionComponent;
  @ViewChild('monthSelection') monthSelection: MonthSelectionComponent;
  @ViewChild('weekSelection') weekSelection: WeekSelectionComponent;
  @ViewChild('formToSelection') formToSelection: DateRangeSelectionComponent;

  selectionTypes = DateSelectionTypeEnum;
  time = new UntypedFormControl({ value: moment.tz(moment(), TIMEZONE) });
  texts = TextKeys;

  private subscription = new Subscription();

  constructor() {}

  ngOnInit() {
    this.setEndOfDay();
  }

  ngAfterViewInit() {
    this.subscription.add(
      this.selectionList.selectionChange.subscribe(
        (s: MatSelectionListChange) => {
          this.selectionList.deselectAll();
          s.options[0].selected = true;
        }
      )
    );

    this.selectionList.options.forEach((o) => {
      if (this.defaultItem) {
        if (o.value === this.defaultItem) {
          o.selected = true;
          this.newItemSelected([o]);
        }
      } else {
        if (o.value === DateSelectionTypeEnum.Year) {
          o.selected = true;
          this.newItemSelected([o]);
        }
      }
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onTimeChange() {
    let dateRange = this.getDateRange();
    this.onChange(dateRange);
  }

  onChange(dateRange: DateRange) {
    const datePart = moment.tz(dateRange.end, TIMEZONE);
    const timeString = this.time.value;
    const combined = moment.tz(
      `${datePart.format('YYYY-MM-DD')}T${timeString}:59`,
      TIMEZONE
    );
    dateRange.end = combined.format();
    this.dateRangeChanged.emit(dateRange);
  }

  newItemSelected(selected: MatListOption[]) {
    selected
      .filter((o) => o.selected === true)
      .map((o) => {
        this.yearSelection.disable();
        this.monthSelection.disable();
        this.weekSelection.disable();
        this.formToSelection.disable();

        if (o.value === DateSelectionTypeEnum.Year) {
          this.yearSelection.enable();
        }
        if (o.value === DateSelectionTypeEnum.Month) {
          this.monthSelection.enable();
        }
        if (o.value === DateSelectionTypeEnum.Week) {
          this.weekSelection.enable();
        }
        if (o.value === DateSelectionTypeEnum.Range) {
          this.formToSelection.enable();
        }
      });

    if (this.getDateRange()) {
      this.onChange(this.getDateRange());
    }
  }

  disableAllExceptYear() {
    this.yearSelection.disableRadio();
    this.monthSelection.disable();
    this.weekSelection.disable();
    this.formToSelection.disable();

    this.selectionList.options.forEach((o) => {
      if (o.value !== DateSelectionTypeEnum.Year) {
        o.disabled = true;
        o.selected = false;
      } else {
        o.selected = true;
      }
    });
  }

  disableAll() {
    this.yearSelection.disable();
    this.monthSelection.disable();
    this.weekSelection.disable();
    this.formToSelection.disable();

    this.selectionList.options.forEach((o) => {
      if (o.value !== DateSelectionTypeEnum.Year) {
        o.disabled = true;
      }
    });
  }

  enableAll() {
    this.yearSelection.disable();
    this.monthSelection.disable();
    this.weekSelection.disable();
    this.formToSelection.disable();

    this.selectionList.options.forEach((o) => {
      if (o.selected) {
        if (o.value === DateSelectionTypeEnum.Year) {
          this.yearSelection.enable();
        }

        if (o.value === DateSelectionTypeEnum.Month) {
          this.monthSelection.enable();
        }

        if (o.value === DateSelectionTypeEnum.Week) {
          this.weekSelection.enable();
        }

        if (o.value === DateSelectionTypeEnum.Range) {
          this.formToSelection.enable();
        }
      }
    });

    this.selectionList.options.forEach((o) => {
      if (o.value !== DateSelectionTypeEnum.Year) {
        o.disabled = false;
      }
    });
  }

  getYear(): string {
    return this.yearSelection.getYear();
  }

  getDateRange(): DateRange {
    let range: DateRange;
    this.selectionList.selectedOptions.selected.map((o) => {
      if (o.value === DateSelectionTypeEnum.Total) {
        range = {
          start: moment(new Date(1990, 1, 1))
            .startOf('month')
            .format(),
          end: moment(new Date()).endOf('month').format(),
        } as DateRange;
      }

      if (o.value === DateSelectionTypeEnum.Year) {
        range = this.yearSelection.getDateRange();
      }
      if (o.value === DateSelectionTypeEnum.Month) {
        range = this.monthSelection.getDateRange();
      }
      if (o.value === DateSelectionTypeEnum.Week) {
        range = this.weekSelection.getDateRange();
      }
      if (o.value === DateSelectionTypeEnum.Range) {
        range = this.formToSelection.getDateRange();
      }
    });
    return range;
  }

  fullDayChange() {
    this.setEndOfDay();
  }

  setEndOfDay() {
    this.time.setValue(
      moment
        .tz(moment(), TIMEZONE)
        .set({ hour: 23, minute: 59, second: 59 })
        .format('HH:mm')
    );
  }
}
